import { useNavigate } from 'react-router-dom';
import {
  LogIn, LogOut, Target, CalendarClock, Phone, Briefcase, Trash2, CalendarRange,
  CheckCircle2, ListChecks, Radar, CalendarDays, Coffee, MessageSquare, FileText,
  Activity as ActivityIcon, type LucideIcon,
} from 'lucide-react';
import { WidgetContainer } from '../../infrastructure/widgets/WidgetContainer';
import { useRecentActivities } from './hooks/useRecentActivities';
import { WidgetConfig } from '../../infrastructure/contracts/widget.contract';
import { cn } from '@/shared/utils/utils';
import { ROUTES } from '@/routes/paths';
import type { Activity } from './adapters/recentActivitiesAdapter';

export const recentActivitiesConfig: WidgetConfig = {
  id: 'dash-recent-activities',
  title: 'Activity Timeline',
  category: 'timeline',
  description: 'Your recent activity',
  refreshStrategy: 'onFocus',
  defaultSize: { w: 4, h: 4 },
  priority: 4,
};

// action_type → Lucide icon (replaces the former Material Symbols).
const ACTION_ICON: Record<string, LucideIcon> = {
  login: LogIn, checkin: LogIn, checkout: LogOut,
  lead_status: Target, follow_up_set: CalendarClock, call_logged: Phone,
  deal_create: Briefcase, deal_stage: Briefcase, deal_update: Briefcase, deal_delete: Trash2,
  leave_submit: CalendarRange, leave_admin_review: CalendarRange, leave_sa_review: CalendarRange,
  task_update: ListChecks, task_done: CheckCircle2,
  scan_start: Radar, scan_done: Radar,
  meeting_create: CalendarDays, meeting_done: CalendarDays,
  break_start: Coffee, break_end: Coffee,
  comment: MessageSquare, doc_create: FileText,
};

const TYPE_STYLES: Record<Activity['type'], { ring: string; text: string }> = {
  success: { ring: 'bg-success/10', text: 'text-success' },
  info: { ring: 'bg-primary/10', text: 'text-primary' },
  warning: { ring: 'bg-warning/10', text: 'text-warning' },
  error: { ring: 'bg-danger/10', text: 'text-danger' },
};

// Map backend entity types to their module routes for click-through.
const ENTITY_ROUTE: Record<string, string> = {
  lead: ROUTES.leads, deal: ROUTES.deals, task: ROUTES.tasks, project: ROUTES.projects,
  meeting: ROUTES.meetings, demo: ROUTES.demos, contact: ROUTES.contacts,
};

function dayBucket(iso: string): 'Today' | 'Yesterday' | 'Earlier' {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Earlier';
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const t = d.getTime();
  if (t >= startToday) return 'Today';
  if (t >= startToday - 86_400_000) return 'Yesterday';
  return 'Earlier';
}

const GROUP_ORDER: Array<'Today' | 'Yesterday' | 'Earlier'> = ['Today', 'Yesterday', 'Earlier'];

export function RecentActivitiesWidget() {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useRecentActivities();

  const activities = (data?.activities ?? []).slice(0, 12);
  const groups = GROUP_ORDER.map((label) => ({
    label,
    items: activities.filter((a) => dayBucket(a.createdAt) === label),
  })).filter((g) => g.items.length > 0);

  return (
    <WidgetContainer
      config={recentActivitiesConfig}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error?.message}
      onRefresh={() => {
        refetch();
      }}
    >
      {activities.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
          <ActivityIcon className="h-6 w-6 text-on-surface-variant" />
          <p className="text-body-sm text-on-surface-variant">No recent activity.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <section key={group.label}>
              <p className="mb-2 text-label-caps uppercase tracking-wider text-on-surface-variant/70">
                {group.label}
              </p>
              <ul className="relative flex flex-col">
                {group.items.map((a) => {
                  const Icon = ACTION_ICON[a.action] ?? ActivityIcon;
                  const style = TYPE_STYLES[a.type];
                  const route = a.entityType ? ENTITY_ROUTE[a.entityType] : undefined;
                  const clickable = !!route;
                  return (
                    <li
                      key={a.id}
                      onClick={clickable ? () => navigate(route!) : undefined}
                      onKeyDown={
                        clickable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(route!);
                              }
                            }
                          : undefined
                      }
                      role={clickable ? 'button' : undefined}
                      tabIndex={clickable ? 0 : undefined}
                      className={cn(
                        'group flex items-start gap-3 rounded-card-sm px-2 py-2 transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                        clickable && 'cursor-pointer hover:bg-surface-variant',
                      )}
                    >
                      <span
                        className={cn(
                          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                          style.ring,
                          style.text,
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-data-md leading-snug text-on-surface">{a.message}</p>
                        <div className="mt-0.5 flex items-center gap-2">
                          <span className="text-body-sm text-on-surface-variant">{a.timestamp}</span>
                          {a.entityType && (
                            <span className="rounded-full bg-surface-variant px-2 py-0.5 text-label-caps uppercase tracking-wide text-on-surface-variant">
                              {a.entityType}
                            </span>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </WidgetContainer>
  );
}
